import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { CurrencyInput } from "@/components";
import { useClinic } from '@/components/Context/ClinicaContext';
import { useUser } from '@/components/Context/UserContext';
import { formatCurrencyBR } from '@/utils/formatUtils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import {
  FaCalendarAlt,
  FaExchangeAlt,
  FaHospital,
  FaUser,
  FaWallet,
} from 'react-icons/fa';
import CentroCustoSelect from '../../CentroCusto/CentroCustoSelect';
import {
  getContaBancoLinha, isContaCaixa
} from '../Reforco/contaCaixaReforcoUtils';

import { request } from '@/services/fetch';

function TransferenciaModal({ children, onSuccess }) {

  const { isOpen, onOpen, onClose } = useDisclosure();

  const OBS_MAX = 500;

  const [data, setData] = useState(new Date());
  const [usuario, setUsuario] = useState('');
  const [contaOrigem, setContaOrigem] = useState('');
  const [contaDestino, setContaDestino] = useState('');
  const [contasBancarias, setContasBancarias] = useState([]);
  const [centroCustoRefresh] = useState(0);
  const [centroCusto, setCentroCusto] = useState('');
  const [valor, setValor] = useState(0);
  const [observacao, setObservacao] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingContas, setLoadingContas] = useState(false);
  const [clinica, setClinica] = useState('');
  const [erroContas, setErroContas] = useState('');

  const { selectedClinic } = useClinic();
  const { user } = useUser();
  const toast = useToast();
  const formattedDate = format(data, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  const nomeClinicaSelecionada = selectedClinic
    ? selectedClinic.razaoSocial || selectedClinic.nome || ''
    : '';

  const getUserId = () => {
    if (user?._id) return user._id;
    if (user?.id) return user.id;
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const p = JSON.parse(stored);
        return (
          p?.user?._id ||
          p?.user?.id ||
          p?.colaborador?._id ||
          p?.profissionalSaude?._id ||
          p?._id ||
          p?.id
        );
      }
    } catch (e) { }
    return null;
  };

  const getUserName = () => {
    if (user?.nome) return user.nome;
    if (user?.name) return user.name;
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const p = JSON.parse(stored);
        const u = p?.user || p?.colaborador || p?.profissionalSaude || p;
        return u?.nome || u?.name || 'Usuário';
      }
    } catch (e) { }
    return 'Usuário';
  };

  useEffect(() => {
    if (isOpen) {
      setClinica(selectedClinic?._id || '');
      setUsuario(getUserName());
      setData(new Date());
    }
  }, [isOpen, selectedClinic]);

  useEffect(() => {
    if (!isOpen) {
      setValor(0);
      setObservacao('');
      setContaOrigem('');
      setContaDestino('');
      setCentroCusto('');
    }
  }, [isOpen]);

  useEffect(() => {
    setContasBancarias([]);
    setContaOrigem('');
    setContaDestino('');
    setErroContas('');
    if (!isOpen || !clinica) return;
    let cancelled = false;
    setLoadingContas(true);
    request(`api/financas/contabancaria/clinica/${clinica}`)
      .then((list) => {
        if (!cancelled) setContasBancarias(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) {
          setContasBancarias([]);
          setErroContas('Não foi possível carregar as contas bancárias. Feche e abra a transferência para tentar novamente.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingContas(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, clinica]);

  const contasAtivas = clinica === selectedClinic?._id
    ? contasBancarias.filter((c) => c.ativa !== false && !isContaCaixa(c))
    : [];
  const contasInsuficientes = contasAtivas.length < 2;
  const avisoContas = 'Para transferir, a clínica precisa ter pelo menos duas contas bancárias ativas, além da conta caixa.';
  const contaOrigemSelecionada = contasAtivas.find(
    (c) => String(c._id) === String(contaOrigem),
  );
  const contaDestinoSelecionada = contasAtivas.find(
    (c) => String(c._id) === String(contaDestino),
  );
  const saldoDisponivelOrigem =
    contaOrigemSelecionada != null
      ? Number(
        contaOrigemSelecionada.saldoAtual ??
        contaOrigemSelecionada.saldoInicial ??
        0,
      )
      : null;
  const saldoDisponivelDestino =
    contaDestinoSelecionada != null
      ? Number(contaDestinoSelecionada.saldoAtual ?? contaDestinoSelecionada.saldoInicial ?? 0)
      : null;
  const maxCentavosDisponiveis =
    contaOrigem && saldoDisponivelOrigem != null
      ? Math.max(0, saldoDisponivelOrigem)
      : null;

  const valorReais = valor;
  const saldoFinalOrigem =
    saldoDisponivelOrigem != null ? saldoDisponivelOrigem - valorReais : null;
  const saldoFinalDestino =
    saldoDisponivelDestino != null ? saldoDisponivelDestino + valorReais : null;
  const feedbackNegativo =
    valor > 0 &&
    saldoDisponivelOrigem != null &&
    valorReais > saldoDisponivelOrigem + 0.009;
  const resumoStatus = feedbackNegativo
    ? 'error'
    : valor > 0 && contaOrigem && contaDestino
      ? 'success'
      : 'info';

  const handleValorChange = (centavos) => {
    const c = Math.max(0, centavos);
    if (maxCentavosDisponiveis != null) {
      setValor(Math.min(c, maxCentavosDisponiveis));
    } else {
      setValor(c);
    }
  };

  useEffect(() => {
    if (!isOpen || maxCentavosDisponiveis == null) return;
    setValor((prev) =>
      prev > maxCentavosDisponiveis ? maxCentavosDisponiveis : prev,
    );
  }, [isOpen, contaOrigem, maxCentavosDisponiveis]);


  const handleSubmit = async () => {
    if (loadingContas || contasInsuficientes || !contaOrigemSelecionada || !contaDestinoSelecionada) {
      toast({ title: 'Transferência indisponível', description: erroContas || avisoContas, status: 'warning', duration: 5000, isClosable: true });
      return;
    }
    const val = valor;
    if (!val || val <= 0) {
      toast({
        title: 'Informe o valor.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (feedbackNegativo) {
      toast({
        title: 'Saldo insuficiente',
        description: `O valor não pode ser maior que o disponível na origem (${formatCurrencyBR(saldoDisponivelOrigem, true)}).`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (!clinica) {
      toast({
        title: 'Selecione uma clínica.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!contaOrigem || !contaDestino) {
      toast({
        title: 'Selecione conta de origem e destino.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (contaOrigem === contaDestino) {
      toast({
        title: 'Origem e destino devem ser diferentes.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const userId = getUserId();
    if (!userId) {
      toast({
        title: 'Usuário não identificado. Faça login novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    try {
      setLoading(true);
      await request(`api/financas/contabancaria/transferencia`, {
        method: 'POST',
        body: JSON.stringify({
          contaOrigemId: contaOrigem,
          contaDestinoId: contaDestino,
          valor: val,
          data: data.toISOString(),
          clinicaId: clinica,
          usuarioId: userId,
          centroCusto: centroCusto || undefined,
          observacao: observacao.trim() || undefined,
        }),
      });
      toast({
        title: 'Transferência realizada com sucesso!',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível realizar a transferência',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const contasDestinoOpcoes = contasAtivas.filter(
    (c) => String(c._id) !== String(contaOrigem),
  );

  const InfoRow = ({ icon, label, children }) => (
    <HStack align="flex-start" spacing={2} w="100%">
      <Icon as={icon} color="purple.500" mt={0.5} boxSize={4} flexShrink={0} />
      <Flex
        flex={1}
        justify="space-between"
        align="flex-start"
        gap={2}
        flexWrap="wrap"
        minW={0}
      >
        <Text
          fontWeight="semibold"
          fontSize="sm"
          color="gray.700"
          whiteSpace="normal"
        >
          {label}
        </Text>
        <Text fontSize="sm" color="gray.800" textAlign={{ base: "left", md: "right" }} minW={0} overflowWrap="anywhere" flex={{ base: "1 1 100%", md: 1 }}>
          {children}
        </Text>
      </Flex>
    </HStack>
  );

  return (
    <>
      {typeof children === 'function' ? children({ onOpen }) : children}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        closeOnOverlayClick={false}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          borderRadius="lg"
          overflow="hidden"
          w={{ base: 'calc(100vw - 16px)', md: 'calc(100vw - 48px)' }}
          maxW="920px"
          my={{ base: 2, md: 6 }}
          maxH={{ base: 'calc(100dvh - 16px)', md: 'calc(100dvh - 48px)' }}
        >
          <ModalHeader
            bgGradient="linear(to-r, purple.600, purple.500)"
            color="white"
            py={2.5}
            px={4}
            pr={12}
            shadow="sm"
          >
            <HStack spacing={2}>
              <Flex
                align="center"
                justify="center"
                w={8}
                h={8}
                borderRadius="full"
                bg="whiteAlpha.300"
              >
                <Icon as={FaExchangeAlt} boxSize={3.5} />
              </Flex>
              <Text fontWeight="bold" fontSize="md">
                Transferência
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" top={2} />

          <ModalBody py={4} px={{ base: 3, md: 4 }} minH={0} overflowY="auto" overflowWrap="anywhere">
            {loadingContas && contasAtivas.length === 0 ? (
              <Flex justify="center" py={6}>
                <Spinner color="purple.500" />
              </Flex>
            ) : (
              <VStack spacing={3} align="stretch">
                {!loadingContas && clinica && (erroContas || contasInsuficientes) && (
                  <Alert status={erroContas ? 'error' : 'warning'} borderRadius="md"><AlertIcon />{erroContas || avisoContas}</Alert>
                )}
                <Box
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={3}
                  bg="gray.50"
                >
                  <Grid
                    templateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }}
                    gap={{ base: 3, md: 0 }}
                    alignItems="center"
                  >
                    <GridItem minW={0} pr={{ md: 4 }} pb={{ base: 3, md: 0 }}>
                      <VStack align="stretch" spacing={1.5}>
                        <InfoRow icon={FaCalendarAlt} label="Data/Hora:">
                          {formattedDate}
                        </InfoRow>
                        <InfoRow icon={FaHospital} label="Clínica:">
                          {nomeClinicaSelecionada || 'Nenhuma clínica selecionada'}
                        </InfoRow>
                        {!clinica && (
                          <Text fontSize="xs" color="purple.600" pl={6}>
                            Selecione uma clínica no seletor da tela antes de realizar
                            a transferência.
                          </Text>
                        )}
                        <InfoRow icon={FaUser} label="Colaborador/Atendente:">
                          {usuario || getUserName()}
                        </InfoRow>
                      </VStack>
                    </GridItem>

                    <GridItem
                      minW={0}
                      pl={{ md: 4 }}
                      pt={{ base: 3, md: 0 }}
                      borderTopWidth={{ base: '1px', md: 0 }}
                      borderLeftWidth={{ base: 0, md: '1px' }}
                      borderColor="gray.300"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      minH={{ md: '100%' }}
                    >
                      <HStack spacing={3} align="flex-start" justify="center" w="100%">
                        <Icon
                          as={FaWallet}
                          color="purple.500"
                          boxSize={5}
                          mt={1}
                          flexShrink={0}
                        />
                        <VStack spacing={2} align="stretch" flex={1} maxW="220px">
                          <Box textAlign="center">
                            <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                              Saldo na Origem:
                            </Text>
                            {contaOrigem && saldoDisponivelOrigem != null ? (
                              <Text
                                fontSize="lg"
                                fontWeight="bold"
                                color="green.600"
                                fontVariantNumeric="tabular-nums"
                                mt={0.5}
                                lineHeight="shorter"
                              >
                                {formatCurrencyBR(saldoDisponivelOrigem, true)}
                              </Text>
                            ) : (
                              <Text fontSize="xs" color="gray.500" mt={0.5}>
                                Selecione a origem
                              </Text>
                            )}
                          </Box>
                          <Box textAlign="center">
                            <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                              Saldo no Destino:
                            </Text>
                            {contaDestino && saldoDisponivelDestino != null ? (
                              <Text
                                fontSize="lg"
                                fontWeight="bold"
                                color="blue.600"
                                fontVariantNumeric="tabular-nums"
                                mt={0.5}
                                lineHeight="shorter"
                              >
                                {formatCurrencyBR(saldoDisponivelDestino, true)}
                              </Text>
                            ) : (
                              <Text fontSize="xs" color="gray.500" mt={0.5}>
                                Selecione o destino
                              </Text>
                            )}
                          </Box>
                        </VStack>
                      </HStack>
                    </GridItem>
                  </Grid>
                </Box>

                <Grid
                  templateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }}
                  gap={3}
                  alignItems="stretch"
                >
                  <GridItem minW={0} display="flex" flexDirection="column" gap={2}>
                    <FormControl isRequired mb={0}>
                      <FormLabel mb={0.5} fontSize="sm">
                        Conta de origem
                      </FormLabel>
                      <Select
                        size="md"
                        value={contaOrigem}
                        onChange={(e) => {
                          setContaOrigem(e.target.value);
                          if (e.target.value === contaDestino) setContaDestino('');
                        }}
                        placeholder={
                          loadingContas ? 'Carregando...' : 'Selecione a conta de origem'
                        }
                        isDisabled={loadingContas || contasInsuficientes}
                      >
                        {contasAtivas.map((c) => (
                          <option key={c._id} value={c._id}>
                            {getContaBancoLinha(c)}
                          </option>
                        ))}
                      </Select>
                      <FormHelperText mb={0} mt={0.5} fontSize="xs">
                        Conta que enviará o valor
                      </FormHelperText>
                    </FormControl>

                    <FormControl isRequired mb={0}>
                      <FormLabel mb={0.5} fontSize="sm">
                        Conta de destino
                      </FormLabel>
                      <Select
                        size="md"
                        value={contaDestino}
                        onChange={(e) => setContaDestino(e.target.value)}
                        placeholder={
                          loadingContas ? 'Carregando...' : 'Selecione a conta de destino'
                        }
                        isDisabled={loadingContas || contasInsuficientes || !contaOrigemSelecionada}
                      >
                        {contasDestinoOpcoes.map((c) => (
                          <option key={c._id} value={c._id}>
                            {getContaBancoLinha(c)}
                          </option>
                        ))}
                      </Select>
                      <FormHelperText mb={0} mt={0.5} fontSize="xs">
                        Conta que receberá o valor
                      </FormHelperText>
                    </FormControl>
                  </GridItem>

                  <GridItem minW={0} display="flex" flexDirection="column" gap={2}>
                    <FormControl isRequired mb={0}>
                      <FormLabel mb={0.5} fontSize="sm">
                        Valor
                      </FormLabel>
                      <CurrencyInput
                        value={valor}
                        onChange={handleValorChange}
                        placeholder="0,00"
                      />
                      <FormHelperText mb={0} mt={0.5} fontSize="xs">
                        Digite o valor (dígitos entram pela direita). Não pode
                        exceder o saldo na origem.
                      </FormHelperText>
                    </FormControl>

                    <Box>
                      <CentroCustoSelect
                        clinicaId={clinica}
                        value={centroCusto}
                        onChange={setCentroCusto}
                        refreshKey={centroCustoRefresh}
                      />
                    </Box>
                  </GridItem>
                </Grid>

                <Grid
                  templateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }}
                  gap={3}
                  alignItems="stretch"
                >
                  <GridItem minW={0} display="flex" flexDirection="column">
                    <Alert
                      status={resumoStatus}
                      borderRadius="md"
                      flexDirection="column"
                      alignItems="flex-start"
                      py={2.5}
                      px={3}
                      h="100%"
                      minH="80px"
                      flex="1"
                    >
                      <HStack mb={1} spacing={2}>
                        <AlertIcon boxSize={4} />
                        <Text fontWeight="semibold" fontSize="sm">
                          {feedbackNegativo
                            ? 'Atenção ao saldo'
                            : 'Resumo da operação'}
                        </Text>
                      </HStack>
                      <VStack align="stretch" spacing={0.5} w="100%" pl={6}>
                        <Text fontSize="xs">
                          Saldo na origem:{' '}
                          <Text as="span" fontWeight="semibold">
                            {saldoDisponivelOrigem != null
                              ? formatCurrencyBR(saldoDisponivelOrigem, true)
                              : '—'}
                          </Text>
                        </Text>
                        <Text fontSize="xs">
                          Saldo no destino:{' '}
                          <Text as="span" fontWeight="semibold">
                            {saldoDisponivelDestino != null
                              ? formatCurrencyBR(saldoDisponivelDestino, true)
                              : '—'}
                          </Text>
                        </Text>
                        <Text fontSize="xs">
                          Valor da transferência:{' '}
                          <Text as="span" fontWeight="semibold">
                            {formatCurrencyBR(valorReais, true)}
                          </Text>
                        </Text>
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color={feedbackNegativo ? 'red.600' : 'green.700'}
                        >
                          Saldo final da conta de origem:{' '}
                          {saldoFinalOrigem != null && contaOrigem
                            ? feedbackNegativo
                              ? `-${formatCurrencyBR(Math.abs(saldoFinalOrigem), true)}`
                              : formatCurrencyBR(saldoFinalOrigem, true)
                            : '—'}
                        </Text>
                        <Text fontSize="xs" fontWeight="bold" color="blue.700">
                          Saldo final da conta de destino:{' '}
                          {saldoFinalDestino != null && contaDestino
                            ? formatCurrencyBR(saldoFinalDestino, true)
                            : '—'}
                        </Text>
                      </VStack>
                    </Alert>
                  </GridItem>

                  <GridItem minW={0}>
                    <FormControl h="100%" display="flex" flexDirection="column" mb={0}>
                      <FormLabel mb={0.5} fontSize="sm">
                        Observação
                      </FormLabel>
                      <Box position="relative" flex="1">
                        <Textarea
                          value={observacao}
                          onChange={(e) =>
                            setObservacao(e.target.value.slice(0, OBS_MAX))
                          }
                          placeholder="Ex: Transferência para saque"
                          rows={3}
                          maxLength={OBS_MAX}
                          flex="1"
                          minH="80px"
                          size="sm"
                        />
                        <Text
                          position="absolute"
                          bottom={2}
                          right={3}
                          fontSize="xs"
                          color="gray.400"
                        >
                          {observacao.length}/{OBS_MAX}
                        </Text>
                      </Box>
                    </FormControl>
                  </GridItem>
                </Grid>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter py={3} px={4} gap={2} flexShrink={0} flexDirection={{ base: "column", sm: "row" }} alignItems={{ base: "stretch", sm: "center" }}>
            <Button variant="outline" colorScheme="purple" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleSubmit}
              isLoading={loading}
              isDisabled={
                loadingContas ||
                contasInsuficientes ||
                !contaOrigemSelecionada ||
                !contaDestinoSelecionada ||
                contaOrigem === contaDestino ||
                valor <= 0 ||
                !clinica ||
                !contaOrigem ||
                !contaDestino ||
                feedbackNegativo ||
                (maxCentavosDisponiveis != null && maxCentavosDisponiveis === 0)
              }
            >
              Confirmar Transferência
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default TransferenciaModal;
